import { createContext, useContext } from 'react'
import { CHAIN_NODE_SUFFIX } from 'app/lib/workflows/stepChecks'
import { logWarn } from 'lib/logging'

// Live run visualization: as a real run executes, the engine emits a WorkflowRunEvent per node
// (nodeId + status). Those nodeIds are the same ids we serialize onto the canvas, so we can light each
// node up as the run passes through it — turning "read a timeline" into "watch it actually happen".

export type RunEventLike = {
  nodeId?: string | null
  nodeType?: string | null
  status?: string | null
  timestamp?: string | null
  /** Engine detail payload. Arrives as a JSON string (JSON scalar) — tolerated as an object too. */
  outputJson?: unknown
}

/** The engine's raw status needs two corrections observed from live event streams:
 *  - A wait-style node reports `completed` the moment it SCHEDULES its wait — the parked truth lives in
 *    outputJson ({"waiting":true,"resumeAt":…}). Surface that as "waiting" so the canvas doesn't show a
 *    green Done on a node that's holding the run (observed: wait / wait_for_call_result).
 *  - Cancelling a run emits a `cancel`-typed event with status `completed` on the node it stopped at —
 *    surface that as "cancelled", not Done.
 *  - A send step that stands down (no consent, opted out, no phone, …) reports `completed` with
 *    {"skipped":true,"reason":…} — nothing was sent, so surface that as "skipped", not Done. */
export function runEventStatus(event: RunEventLike): string | undefined {
  if (event.nodeType === `cancel`) return `cancelled`
  if (!event.status) return undefined
  const detail = parseDetail(event.outputJson)
  if (detail?.waiting === true) return `waiting`
  if (detail?.skipped === true) return `skipped`
  return event.status
}

/** The engine's JSON detail payload as an object, or undefined when absent or malformed. */
function parseDetail(raw: unknown): Record<string, unknown> | undefined {
  try {
    const detail = typeof raw === `string` ? (JSON.parse(raw) as unknown) : raw
    return detail && typeof detail === `object` ? (detail as Record<string, unknown>) : undefined
  } catch (err) {
    logWarn(`Run event outputJson parse failed`, err)
    return undefined
  }
}

type RunEventDetail = { tone: `failed` | `exited`; text: string }

/** Engine reason and exit codes in words, keyed lowercase. A code not listed here (INVALID_MESSAGE_CREATE_STATUS,
 *  "next node not found: n2") is shown verbatim: it is the exact string someone will grep the API for. */
export const RUN_REASON_TEXT: Readonly<Record<string, string>> = {
  no_marketing_consent: `contact has no SMS marketing consent`,
  opted_out: `contact opted out of SMS`,
  never_opted_in: `contact never opted in to SMS`,
  no_phone: `contact has no phone number`,
  quiet_hours: `outside the send window`,
  agent_busy: `agent is already on this thread`,
  contact_not_found: `contact not found`,
  no_contact: `contact not found`,
  location_not_found: `location not found`,
  no_ring_number: `the call step has no number to ring`,
  no_agent_prompt: `no agent prompt configured`,
  location_lost_sms_agent: `location no longer has SMS Agent`,
  location_lost_inbox: `location no longer has Inbox`,
  no_texting_number: `location has no texting number`,
  no_template: `the paperwork packet is empty or has unpublished forms`,
  not_supported_for_this_practice_system: `the practice system can't take this change`,
  workspace_sending_paused: `email sending is paused for this workspace`,
  location_not_configured: `location isn't set up to send email`,
  domain_not_verified: `the sending domain isn't verified`,
  send_failed: `send failed`,
}

/** Why a step failed or was skipped, in one line, or undefined for a step that simply ran. The engine
 *  writes it into outputJson: {"error":…} on failure, {"skipped":true,"reason":…} on a stand-down. */
export function runEventDetail(event: RunEventLike): RunEventDetail | undefined {
  const detail = parseDetail(event.outputJson)
  if (!detail) return undefined
  if (typeof detail.error === `string` && detail.error) {
    return { tone: `failed`, text: `Failed: ${RUN_REASON_TEXT[detail.error.toLowerCase()] ?? detail.error}` }
  }
  if (detail.skipped === true) {
    const reason = typeof detail.reason === `string` ? detail.reason : ``
    const words = RUN_REASON_TEXT[reason.toLowerCase()] ?? reason.toLowerCase().replace(/_/g, ` `)
    return { tone: `exited`, text: words ? `Skipped: ${words}` : `Skipped` }
  }
  return undefined
}

/** Latest status per engine node id, from a run's events (events accrue over time; keep the newest
 *  per node by timestamp). Enrollment events are excluded — they carry the FIRST node's id (observed
 *  live) and would wrongly mark it in-progress before it runs; see `hasEnrollment` for the trigger. */
export function deriveRunNodeStatus(events: readonly RunEventLike[]): Map<string, string> {
  const latest = new Map<string, { status: string; ts: number }>()
  for (const event of events) {
    if (event.nodeType === `enrollment`) continue
    const status = runEventStatus(event)
    if (!event.nodeId || !status) continue
    const nodeId = event.nodeId.replace(CHAIN_NODE_SUFFIX, ``)
    const ts = event.timestamp ? Date.parse(event.timestamp) || 0 : 0
    const prev = latest.get(nodeId)
    if (!prev || ts >= prev.ts) latest.set(nodeId, { status, ts })
  }
  return new Map([...latest].map(([id, value]) => [id, value.status]))
}

/** Whether the run enrolled — lights the canvas trigger node, whose own id never appears in events (the
 *  trigger is pruned from the engine graph; enrollment is its own event, tagged with the first node's id). */
export function hasEnrollment(events: readonly RunEventLike[]): boolean {
  return events.some((event) => event.nodeType === `enrollment`)
}

export type RunTone = `running` | `waiting` | `done` | `failed` | `exited`

/** Map a raw engine status string to a visual tone (or undefined for "no status"). Fuzzy on purpose —
 *  the engine's exact status vocabulary isn't pinned, so we match on meaning. */
export function runTone(status: string | undefined): RunTone | undefined {
  if (!status) return undefined
  const s = status.toLowerCase()
  if (/fail|error/.test(s)) return `failed`
  if (/wait|pending/.test(s)) return `waiting`
  if (/done|complete|success|sent|ok/.test(s)) return `done`
  if (/exit|cancel|skip/.test(s)) return `exited`
  return `running`
}

/** engine-nodeId → raw status for the run currently being watched (empty map when not watching). */
export const RunStatusContext = createContext<Map<string, string>>(new Map())
export const useRunStatus = () => useContext(RunStatusContext)

/** How long a run may sit without moving before we call it stalled. Generous on purpose: a healthy
 *  step executes in seconds (measured repeatedly on dev), and a wait that is genuinely parked reports
 *  `waiting` with a resumeAt rather than sitting at `running`. */
const STALL_MINUTES = 10

/** A run that has stopped moving, said in a sentence, or null when nothing is wrong.
 *
 *  This exists because of TRK-5563: a run enrolled from a call event never reached its send step and
 *  sat at `running` for half an hour. It rendered exactly like a healthy run — same pill, same "At:
 *  n1" — so nothing on screen distinguished "working" from "dead". Four of them accumulated over two
 *  days before anyone noticed, and only then because someone went looking.
 *
 *  Two shapes:
 *   - `running` and untouched for a while: the step never started or never finished.
 *   - `waiting` past its own resumeAt: the wake-up did not happen. A cap that holds until midnight is
 *     healthy; one that is still holding at noon the next day is not. */
export function runStall(
  run: { status?: string; updatedDate?: string; resumeAt?: string | null },
  now: Date = new Date(),
): string | null {
  const minutesSince = (iso?: string | null) =>
    iso ? Math.floor((now.getTime() - new Date(iso).getTime()) / 60000) : null

  if (run.status === `waiting`) {
    const overdue = minutesSince(run.resumeAt)
    if (overdue !== null && overdue > STALL_MINUTES) {
      return `Overdue: should have resumed ${formatMinutes(overdue)} ago`
    }
    return null
  }

  if (run.status === `running`) {
    const idle = minutesSince(run.updatedDate)
    if (idle !== null && idle > STALL_MINUTES) return `Stalled: no progress for ${formatMinutes(idle)}`
  }
  return null
}

function formatMinutes(total: number): string {
  if (total < 60) return `${total}m`
  const hours = Math.floor(total / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}
